import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const REVIEWS = [
  {
    name: "Marcus T.",
    handle: "@marcust_trades",
    role: "Verified Buyer",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=160&h=160&fit=crop&crop=faces&auto=format&q=80",
    text:
      "I bought every trading course on YouTube before this. None of them explained risk management as clearly as Chapter 6. Worth every dollar.",
  },
  {
    name: "Aisha R.",
    handle: "@aisha.markets",
    role: "Verified Buyer",
    rating: 5,
    avatar: null,
    text:
      "I'm a complete beginner. Two weeks in and I can finally read a candlestick chart without guessing. The visuals make it click.",
  },
  {
    name: "Daniel K.",
    handle: "Verified Buyer",
    role: "Software Engineer",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop&crop=faces&auto=format&q=80",
    text:
      "Read it cover to cover in two evenings. The Common Beginner Mistakes chapter alone saved me from blowing up my account.",
  },
  {
    name: "Priya S.",
    handle: "Verified Buyer",
    role: "Nurse · Part-time Trader",
    rating: 5,
    avatar: null,
    text:
      "Honestly the first guide that doesn't try to sell me on a 'secret indicator.' Just clean fundamentals and habits.",
  },
  {
    name: "Jordan M.",
    handle: "Verified Buyer",
    role: "Full-time Office Manager",
    rating: 4,
    avatar: null,
    text:
      "Built my routine around the framework in Chapter 7. I'm not rich yet, but I finally feel like I know what I'm doing every morning.",
  },
  {
    name: "Sofia L.",
    handle: "@sofialiu_fx",
    role: "Verified Buyer",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=160&h=160&fit=crop&crop=faces&auto=format&q=80",
    text:
      "I work a 9-to-5 and trade in the evenings. The routine and psychology sections are gold for someone juggling both.",
  },
  {
    name: "Kenji A.",
    handle: "Verified Buyer",
    role: "Recent Grad",
    rating: 5,
    avatar: null,
    text:
      "$199 felt steep at first. After reading it, I would've paid double. The worksheets are the real value.",
  },
  {
    name: "Elena R.",
    handle: "Verified Buyer",
    role: "Verified Buyer",
    rating: 5,
    avatar: null,
    text:
      "Zero fluff. Every page taught me something I could use the next day. Wish I had this 3 years ago.",
  },
  {
    name: "Tomás V.",
    handle: "@tomasv.trades",
    role: "Verified Buyer",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=160&h=160&fit=crop&crop=faces&auto=format&q=80",
    text:
      "The liquidity & volatility chapter finally made sense of why my trades kept getting stopped out at the worst moment.",
  },
];

function Stars({ count }) {
  return (
    <div className="flex gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < count ? "text-emerald-400 fill-emerald-400" : "text-white/15"
          }`}
        />
      ))}
    </div>
  );
}

function Initials({ name }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="w-11 h-11 rounded-full bg-emerald-500/15 border border-emerald-500/30 grid place-items-center font-display font-bold text-emerald-300 text-sm shrink-0">
      {initials}
    </div>
  );
}

export default function Reviews() {
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 bg-[#070707] border-y border-white/5"
    >
      {/* Subtle glow */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-32 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-[140px] -z-10"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
        >
          <div>
            <div className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-semibold font-display mb-4">
              Reader Reviews
            </div>
            <h2 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              Real readers. <br className="hidden md:block" />
              <span className="text-emerald-400">Real results.</span>
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end">
              <Stars count={5} />
              <div className="text-white/60 text-sm mt-1">
                <span className="text-white font-display font-bold">4.9</span> · 2,847 verified
                buyers
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {REVIEWS.map((r, i) => (
            <motion.figure
              key={r.name}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="group relative flex flex-col p-6 rounded-2xl bg-[#0c0c0c] border border-white/10 hover:border-emerald-500/30 hover:-translate-y-1 transition-all duration-300"
            >
              <Quote
                className="absolute top-5 right-5 w-5 h-5 text-emerald-500/20 group-hover:text-emerald-500/40 transition"
                aria-hidden
              />
              <Stars count={r.rating} />
              <blockquote className="mt-4 text-white/75 text-sm md:text-[15px] leading-relaxed">
                "{r.text}"
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-white/5 flex items-center gap-3">
                {r.avatar ? (
                  <img
                    src={r.avatar}
                    alt={r.name}
                    loading="lazy"
                    className="w-11 h-11 rounded-full object-cover border border-white/10 shrink-0"
                  />
                ) : (
                  <Initials name={r.name} />
                )}
                <div className="min-w-0">
                  <div className="font-display font-semibold text-white text-sm truncate">
                    {r.name}
                  </div>
                  <div className="text-xs text-white/45 truncate">{r.role}</div>
                </div>
                <span className="ml-auto text-[10px] font-display tracking-widest uppercase text-emerald-400/80 shrink-0">
                  ✓ Verified
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
